import React from 'react';
import { useTranslation } from 'react-i18next';
import { flairNameProps, flairPlateProps } from '../../utils/flair';
import ThemePreview from '../common/ThemePreview';
import FlairEmblem from './FlairEmblem';
import './FlairStyles.css';

/**
 * En flair-vara renderad i sin faktiska stil.
 *
 * Delas av butikskorten och profilens inventory. Bodde tidigare som en lokal
 * funktion i `ShopPage.jsx`; bröts ut när inventoryt behövde samma sak, för en
 * andra kopia hade driftat isär från den första och gjort att butiken och
 * profilen visade samma vara olika.
 *
 * Renderar bara själva varan — ingen ram, ingen bakgrund. Måtten som beror på
 * sammanhanget (badge-emojin är stor på butikskortet, lagom i en inventory-rad)
 * kommer från containerns CSS, inte härifrån.
 *
 * @param {object} props
 * @param {string} props.kind - `flair_kind`: emblem/badge/name_color/title_plate/title.
 * @param {string} props.value - `flair_value`: filnamn, emoji, hex/presetnyckel,
 *   plattnyckel eller titeltext.
 * @param {string} [props.name] - Varans visningsnamn, används som tooltip.
 * @param {string} [props.ownName] - Spelarens eget namn. Namnstilar renderas med
 *   det: en trappa där priset följer hur mycket stilen gör är obegriplig om man
 *   inte ser den på sitt eget namn.
 * @returns {JSX.Element} Previewen.
 */
const FlairPreview = ({ kind, value, name, ownName }) => {
  const { t } = useTranslation();

  if (kind === 'theme') {
    // Temat är den enda flair-sorten som inte syns vid nicket utan målar om
    // hela sajten, så previewen är en miniatyr av sajten — inte ett smakprov
    // på ett namn. Se ThemePreview.
    return <ThemePreview themeId={value} label={name} />;
  }
  if (kind === 'emblem') {
    return (
      <FlairEmblem emblem={value} title={name} className="flair-emblem--preview" />
    );
  }
  if (kind === 'badge') {
    return <span className="flair-preview-badge">{value}</span>;
  }
  if (kind === 'name_color') {
    return (
      <span {...flairNameProps(value, 'shop-flair-name-preview')}>{ownName}</span>
    );
  }
  if (kind === 'title_plate') {
    return (
      <span {...flairPlateProps(value, null, 'shop-flair-plate-preview')}>
        {t('shop.platePreviewTitle')}
      </span>
    );
  }
  // Titel (och okänd sort): hakparenteserna är inte dekor — de är exakt vad
  // profilen ritar när spelaren saknar titelplatta (se PlayerProfile.jsx).
  return <span className="shop-flair-title-preview">「{value}」</span>;
};

export default FlairPreview;
